import type { AttemptView, DailyRewardUsage, PublicOfferView } from "@townsy/contracts";
import { formatCoins } from "../lib/wallet";
import { OFFER_KIND_LABEL, eligibilityCopy, rewardText, type EligibilityCopy } from "./copy";

/**
 * The "Earn coins" list and the store's task list as display rows (pure; unit-tested). Everything comes
 * from the server's read models: offers, the viewer's eligibility, the UTC-day allowance and the active
 * attempt. Nothing is invented: an empty list says so.
 */

export interface DailyProgress {
  /** Coins granted today (UTC) from sponsored tasks. */
  granted: number;
  /** Coins reserved by the task in progress. */
  reserved: number;
  cap: number;
  /** granted + reserved, clamped to the cap. */
  used: number;
  /** 0..100 for the meter. */
  percent: number;
  /** "30 / 100 coins today (UTC)". */
  text: string;
  /** Extra line about the reservation, or null. */
  reservedText: string | null;
  remaining: number;
  day: string;
}

export function dailyProgress(daily: DailyRewardUsage | null | undefined): DailyProgress | null {
  if (!daily) return null;
  const cap = Math.max(0, Math.trunc(daily.capCoins));
  const granted = Math.max(0, Math.trunc(daily.grantedCoins));
  const reserved = Math.max(0, Math.trunc(daily.reservedCoins));
  const used = Math.min(cap, granted + reserved);
  return {
    granted,
    reserved,
    cap,
    used,
    percent: cap > 0 ? Math.round((Math.min(cap, granted) / cap) * 100) : 0,
    text: `${granted} / ${cap} sponsored coins today (UTC)`,
    reservedText: reserved > 0 ? `${formatCoins(reserved)} reserved for your task in progress` : null,
    remaining: Math.max(0, cap - used),
    day: daily.day,
  };
}

export interface OfferRow {
  offer: PublicOfferView;
  key: string;
  kindLabel: string;
  reward: string;
  sponsor: string;
  /** "Sponsored by X" / "Townsy task". */
  sponsorLine: string;
  copy: EligibilityCopy;
  /** The viewer's active attempt is on this offer. */
  active: boolean;
}

export function offerRow(offer: PublicOfferView, ctx: { activeAttempt?: Pick<AttemptView, "offerId" | "attemptId" | "state"> | null; dailyCapCoins?: number | null } = {}): OfferRow {
  const activeHere = Boolean(offer.activeAttemptId) || (ctx.activeAttempt?.state === "active" && ctx.activeAttempt.offerId === offer.offerId);
  return {
    offer,
    key: offer.offerId,
    kindLabel: OFFER_KIND_LABEL[offer.kind],
    reward: rewardText(offer.rewardCoins),
    sponsor: offer.sponsorLabel,
    sponsorLine: offer.isHouse ? "A Townsy task" : `Sponsored by ${offer.sponsorLabel}`,
    copy: eligibilityCopy(offer.eligibility, { activeHere, dailyCapCoins: ctx.dailyCapCoins }),
    active: activeHere,
  };
}

const ORDER: Record<string, number> = { continue: 0, start: 1, show_active: 2, sign_in: 3, verify_email: 3 };

/**
 * Rows for the list: the task in progress first, then the ones the viewer can start (highest reward
 * first), then the rest. Ties keep the server's order (reward, address).
 */
export function earnRows(items: readonly PublicOfferView[], ctx: { activeAttempt?: AttemptView | null; daily?: DailyRewardUsage | null } = {}): OfferRow[] {
  const rows = items.map((o, i) => ({ row: offerRow(o, { activeAttempt: ctx.activeAttempt ?? null, dailyCapCoins: ctx.daily?.capCoins ?? null }), i }));
  rows.sort((a, b) => {
    const ra = a.row.copy.cta ? (ORDER[a.row.copy.cta] ?? 4) : 5;
    const rb = b.row.copy.cta ? (ORDER[b.row.copy.cta] ?? 4) : 5;
    if (ra !== rb) return ra - rb;
    if (a.row.offer.rewardCoins !== b.row.offer.rewardCoins) return b.row.offer.rewardCoins - a.row.offer.rewardCoins;
    return a.i - b.i;
  });
  return rows.map((r) => r.row);
}

/** Summary line above the list ("3 tasks you can start · 1 in progress"). */
export function earnSummary(rows: readonly OfferRow[]): string {
  if (rows.length === 0) return "No sponsored tasks right now.";
  const startable = rows.filter((r) => r.copy.cta === "start").length;
  const active = rows.filter((r) => r.copy.cta === "continue").length;
  const parts = [`${rows.length} ${rows.length === 1 ? "task" : "tasks"} in town`];
  if (startable > 0) parts.push(`${startable} you can start`);
  if (active > 0) parts.push("1 in progress");
  return parts.join(" · ");
}

/** The active attempt's line on the list ("In progress: Quiz at Maple Bakery — 8:12 left"). */
export function activeAttemptLine(a: Pick<AttemptView, "title" | "sponsorLabel" | "state">): string | null {
  if (a.state !== "active") return null;
  return `In progress: “${a.title}” from ${a.sponsorLabel}`;
}
